import React from "react";

/**
 * Shared helpers for swipe-deck card components.
 *
 * Backend already returns `content` localized into the requested language
 * (with English fallback) — see LearningSwipeCardSerializer.get_content —
 * so per-card components don't need to do their own language resolution.
 * Translation only happens once at fetch time; mid-deck language changes
 * are out of scope for v1.
 */

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Reduced-motion

/**
 * Returns true when the OS-level "reduce motion" preference is set.
 * Reactive — re-renders if the preference changes during the session.
 *
 * Used by card components and the player shell to:
 *   - skip confetti
 *   - replace slide transitions with quick fades
 *   - disable spring physics
 */
export function usePrefersReducedMotion() {
  const getMatch = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  };

  const [reduced, setReduced] = React.useState(getMatch);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setReduced(e.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    // Safari < 14 fallback
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return reduced;
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Language-aware micro-copy

const POSITIVE_COPY = {
  english: ["Nice.", "Got it.", "Right on.", "You got this.", "Solid."],
  spanish: ["¡Bien!", "¡Correcto!", "¡Eso es!", "¡Tú puedes!", "¡Perfecto!"],
  chinese: ["很好。", "答对了。", "棒极了！", "你做到了。", "正确！"],
};

const NOT_QUITE_COPY = {
  english: "Not quite.",
  spanish: "Casi.",
  chinese: "差一点。",
};

const COMPLETION_COPY = {
  english: ["Done!", "Nice work.", "All set.", "Way to go.", "You crushed it."],
  spanish: ["¡Listo!", "¡Buen trabajo!", "¡Terminado!", "¡Lo lograste!", "¡Excelente!"],
  chinese: ["完成！", "干得好！", "全部完成。", "你做到了！", "棒极了！"],
};

/**
 * Pick a random positive line for a correct quiz answer. Varied so users
 * don't see the same string repeatedly.
 */
export function getPositiveCopy(language) {
  const pool =
    POSITIVE_COPY[language && language.toLowerCase()] || POSITIVE_COPY.english;
  return pool[Math.floor(Math.random() * pool.length)];
}

export function getNotQuiteCopy(language) {
  const lang = language && language.toLowerCase();
  return NOT_QUITE_COPY[lang] || NOT_QUITE_COPY.english;
}

export function getCompletionCopy(language) {
  const pool =
    COMPLETION_COPY[language && language.toLowerCase()] ||
    COMPLETION_COPY.english;
  return pool[Math.floor(Math.random() * pool.length)];
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Media + content accessors

/**
 * Resolve a media url from a card. The backend serializer returns
 * `media: { uuid, url }` (already a signed URL); this just safely picks
 * the url without exploding on missing fields.
 */
export function getMediaUrl(card) {
  return card?.media?.url || null;
}

export function getThumbnailUrl(card) {
  return card?.thumbnail?.url || null;
}

/**
 * Choices for poll/quiz cards live at content.choices. This wrapper hands
 * back an array even when the field is missing or the wrong shape, so card
 * components don't have to defend against malformed payloads.
 */
export function getChoices(card) {
  const choices = card?.content?.choices;
  return Array.isArray(choices) ? choices : [];
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Visual constants

/**
 * Brand violet — used for all interactive accents in the player
 * (quiz/poll selected option, submit button, completion-screen continue
 * button, confetti). Decks don't carry a per-deck accent. text_only
 * cards are always white (admins can theme via image_text + image
 * layouts if they want color).
 */
export const BRAND_VIOLET = "#6d28d9";

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Top-image band sizing

// Card aspect (9:16). A band that exactly matched a photo's aspect would be
// CARD_ASPECT / (imgWidth / imgHeight) of the card height; clamping that to
// [min, max] keeps the crop small without starving the content below.
export const CARD_ASPECT = 9 / 16;

// Poll/trivia context-image band range. Capped tighter than the split
// image+text card (which goes to 0.6) because these cards carry more content
// underneath — question + up to 4 answers + a Submit button — so the image
// shouldn't be allowed to grow as tall.
export const CONTEXT_BAND_MIN = 0.4;
export const CONTEXT_BAND_MAX = 0.55;

export function aspectBandFraction(naturalWidth, naturalHeight, min, max) {
  if (!naturalWidth || !naturalHeight) return min;
  const fraction = CARD_ASPECT / (naturalWidth / naturalHeight);
  return Math.min(max, Math.max(min, fraction));
}

/**
 * Sizes a top image band to its photo's aspect ratio, clamped to
 * [min, max] of the card height, so the same photo reads consistently and
 * the band eases (via a flex-basis transition on the caller) to fit the
 * image instead of using a fixed fraction. Returns the band fraction plus a
 * ref + onLoad to wire onto the <img>, and `loaded` for optional fade-in.
 *
 * The split image+text card keeps its own copy of this logic (it layers an
 * extra empty-state placeholder on top); poll and trivia share this hook.
 */
export function useImageBand(imageUrl, min, max) {
  const [band, setBand] = React.useState(min);
  const [loaded, setLoaded] = React.useState(false);
  const imgRef = React.useRef(null);
  const handleLoad = React.useCallback(
    (e) => {
      const img = e.currentTarget;
      setBand(aspectBandFraction(img.naturalWidth, img.naturalHeight, min, max));
      setLoaded(true);
    },
    [min, max],
  );
  // Reset on src change, and catch already-cached images whose onLoad may
  // never fire (which would otherwise leave the band stuck at the floor).
  React.useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth) {
      setBand(aspectBandFraction(img.naturalWidth, img.naturalHeight, min, max));
      setLoaded(true);
    } else {
      setBand(min);
      setLoaded(false);
    }
  }, [imageUrl, min, max]);
  return { band, loaded, imgRef, handleLoad };
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// HTML sanitization

/**
 * Tiptap-emitted body markup is rendered via `dangerouslySetInnerHTML`
 * in image+text cards (player + storyboard tiles). The post-deck save
 * endpoint persists `content_json` verbatim — it doesn't sanitize HTML
 * — so a crafted admin API call could store `<img onerror="…">` or
 * similar and have it execute in employees' browsers when the card
 * renders. Sanitize on render with a strict allowlist that matches
 * Tiptap's actual output (paragraph, bold, italic, hard break) so any
 * tag or attribute not in that set gets stripped.
 *
 * Strategy: parse with DOMParser (which never executes scripts), walk
 * the tree, replace disallowed elements with their text content, strip
 * every attribute on every kept element. Output is the safe innerHTML.
 */
const ALLOWED_HTML_TAGS = new Set([
  "P",
  "STRONG",
  "EM",
  "B",
  "I",
  "BR",
  "UL",
  "OL",
  "LI",
]);

export function sanitizeBodyHtml(html) {
  if (typeof html !== "string" || !html) return "";
  if (typeof DOMParser === "undefined") return "";
  // Wrap in a known container so root.innerHTML returns just the
  // sanitized fragment, not the whole document chrome.
  const doc = new DOMParser().parseFromString(
    `<div id="__rw_swipe_body__">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("__rw_swipe_body__");
  if (!root) return "";

  const walk = (node) => {
    // Snapshot children before mutating — replaceChild / removeChild
    // would otherwise reshuffle a live NodeList mid-iteration.
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === 1) {
        if (!ALLOWED_HTML_TAGS.has(child.tagName)) {
          // Disallowed element — keep its text but drop the element
          // (and any attributes / event handlers it carried).
          const text = doc.createTextNode(child.textContent || "");
          node.replaceChild(text, child);
        } else {
          // Strip every attribute. None are needed for the allowlisted
          // tags; this defends against onclick/onerror/etc.
          while (child.attributes.length > 0) {
            child.removeAttribute(child.attributes[0].name);
          }
          walk(child);
        }
      } else if (child.nodeType !== 3) {
        // Drop comments, CDATA, etc. — only elements and text survive.
        node.removeChild(child);
      }
    }
  };
  walk(root);
  return root.innerHTML;
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Haptics

/**
 * Fire a tiny vibration on the device. No-op when the browser doesn't
 * support the Vibration API (most desktops, iOS Safari) so callers can
 * sprinkle these freely without feature-detecting at every call site.
 */
export function vibrate(pattern) {
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.vibrate === "function"
  ) {
    try {
      navigator.vibrate(pattern);
    } catch (_) {
      // Some browsers throw if vibrate is called too often — swallow.
    }
  }
}
